import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import type { UploadFileItem } from '@/client/components/FileUpload';
import type { BatchTaskItem, CsvImportSummary, CsvPromptRow } from './types';
import { parsePromptCsvContent } from './utils/csvParser';

export interface BatchTaskContextValue {
  tasks: BatchTaskItem[];
  selectedTaskIds: string[];
  selectionAnchorId: string | null;
  importFromCsv: (file: File) => Promise<CsvImportSummary>;
  addPromptRows: (rows: CsvPromptRow[]) => string[];
  clearTasks: () => void;
  removeTasks: (taskIds: string[]) => void;
  setSelection: (taskIds: string[], anchorId?: string | null) => void;
  addToSelection: (taskIds: string[]) => void;
  removeFromSelection: (taskIds: string[]) => void;
  setSelectionAnchor: (taskId: string | null) => void;
  updateTasksByIds: (taskIds: string[], updater: (task: BatchTaskItem) => BatchTaskItem) => void;
  setModelForTasks: (taskIds: string[], modelKey: string | null) => void;
  setFormValuesForTasks: (taskIds: string[], modelKey: string, values: unknown) => void;
  updateFormValuesForTasks: (
    taskIds: string[],
    modelKey: string,
    updater: (current: unknown) => unknown
  ) => void;
  updateImagesForTasks: (
    taskIds: string[],
    updater: (current: UploadFileItem[]) => UploadFileItem[]
  ) => void;
}

const BatchTaskContext = createContext<BatchTaskContextValue | undefined>(undefined);

const createTaskId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const createTaskFromRow = (row: CsvPromptRow, importedAt: number): BatchTaskItem => ({
  id: createTaskId(),
  prompt: row.prompt,
  modelKey: null,
  formValues: {},
  images: [],
  status: 'idle',
  error: null,
  taskId: null,
  submittedAt: null,
  meta: {
    csvLine: row.lineNumber,
    csvIndex: row.dataIndex,
    csvRaw: [...row.rawFields],
    importedAt,
  },
});

export const BatchTaskProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [tasks, setTasks] = useState<BatchTaskItem[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [selectionAnchorId, setSelectionAnchorId] = useState<string | null>(null);

  const addPromptRows = useCallback((rows: CsvPromptRow[]): string[] => {
    if (!rows.length) {
      return [];
    }
    const importedAt = Date.now();
    const newIds: string[] = [];
    setTasks((prev) => {
      const next = [...prev];
      for (const row of rows) {
        const task = createTaskFromRow(row, importedAt);
        newIds.push(task.id);
        next.push(task);
      }
      return next;
    });
    if (newIds.length) {
      setSelectedTaskIds((prev) => {
        const existing = new Set(prev);
        const merged = [...prev];
        for (const id of newIds) {
          if (!existing.has(id)) {
            merged.push(id);
          }
        }
        return merged;
      });
      setSelectionAnchorId(newIds[newIds.length - 1]);
    }
    return newIds;
  }, []);

  const importFromCsv = useCallback(
    async (file: File): Promise<CsvImportSummary> => {
      try {
        const text = await file.text();
        const { rows, warnings, errors } = parsePromptCsvContent(text);
        if (errors.length) {
          return { imported: 0, warnings, errors };
        }
        const newIds = addPromptRows(rows);
        return { imported: newIds.length, warnings, errors: [] };
      } catch (error) {
        const message =
          error instanceof Error && error.message ? error.message : '读取 CSV 文件失败。';
        return { imported: 0, warnings: [], errors: [message] };
      }
    },
    [addPromptRows]
  );

  const clearTasks = useCallback(() => {
    setTasks([]);
    setSelectedTaskIds([]);
    setSelectionAnchorId(null);
  }, []);

  const removeTasks = useCallback((taskIds: string[]) => {
    if (!taskIds.length) return;
    const removalSet = new Set(taskIds);
    setTasks((prev) => prev.filter((task) => !removalSet.has(task.id)));
    setSelectedTaskIds((prev) => prev.filter((id) => !removalSet.has(id)));
    setSelectionAnchorId((prev) => (prev && removalSet.has(prev) ? null : prev));
  }, []);

  const setSelection = useCallback((taskIds: string[], anchorId?: string | null) => {
    const unique: string[] = [];
    const seen = new Set<string>();
    for (const id of taskIds) {
      if (seen.has(id)) continue;
      seen.add(id);
      unique.push(id);
    }
    setSelectedTaskIds(unique);
    if (typeof anchorId !== 'undefined') {
      setSelectionAnchorId(anchorId);
    } else {
      setSelectionAnchorId(unique.length ? unique[unique.length - 1] : null);
    }
  }, []);

  const addToSelection = useCallback((taskIds: string[]) => {
    if (!taskIds.length) return;
    setSelectedTaskIds((prev) => {
      const next = [...prev];
      const set = new Set(prev);
      for (const id of taskIds) {
        if (!set.has(id)) {
          next.push(id);
        }
      }
      return next;
    });
    setSelectionAnchorId((prev) => (prev ? prev : taskIds[taskIds.length - 1] ?? null));
  }, []);

  const removeFromSelection = useCallback((taskIds: string[]) => {
    if (!taskIds.length) return;
    const removalSet = new Set(taskIds);
    setSelectedTaskIds((prev) => prev.filter((id) => !removalSet.has(id)));
    setSelectionAnchorId((prev) => (prev && removalSet.has(prev) ? null : prev));
  }, []);

  const updateTasksByIds = useCallback(
    (taskIds: string[], updater: (task: BatchTaskItem) => BatchTaskItem) => {
      if (!taskIds.length) return;
      const targetSet = new Set(taskIds);
      setTasks((prev) => {
        let mutated = false;
        const next = prev.map((task) => {
          if (!targetSet.has(task.id)) {
            return task;
          }
          const updated = updater(task);
          if (updated !== task) {
            mutated = true;
          }
          return updated;
        });
        return mutated ? next : prev;
      });
    },
    []
  );

  const setModelForTasks = useCallback(
    (taskIds: string[], modelKey: string | null) => {
      updateTasksByIds(taskIds, (task) => {
        if (task.modelKey === modelKey) {
          return task;
        }
        return {
          ...task,
          modelKey,
        };
      });
    },
    [updateTasksByIds]
  );

  const setFormValuesForTasks = useCallback(
    (taskIds: string[], modelKey: string, values: unknown) => {
      updateTasksByIds(taskIds, (task) => {
        const nextFormValues = { ...task.formValues, [modelKey]: values };
        const prevValue = task.formValues[modelKey];
        if (prevValue === values) {
          return task;
        }
        return {
          ...task,
          formValues: nextFormValues,
        };
      });
    },
    [updateTasksByIds]
  );

  const updateFormValuesForTasks = useCallback(
    (taskIds: string[], modelKey: string, updater: (current: unknown) => unknown) => {
      updateTasksByIds(taskIds, (task) => {
        const currentValue = task.formValues[modelKey];
        const nextValue = updater(currentValue);
        if (nextValue === currentValue) {
          return task;
        }
        return {
          ...task,
          formValues: {
            ...task.formValues,
            [modelKey]: nextValue,
          },
        };
      });
    },
    [updateTasksByIds]
  );

  const updateImagesForTasks = useCallback(
    (taskIds: string[], updater: (current: UploadFileItem[]) => UploadFileItem[]) => {
      updateTasksByIds(taskIds, (task) => {
        const nextImages = updater(task.images);
        if (nextImages === task.images) {
          return task;
        }
        return {
          ...task,
          images: nextImages,
        };
      });
    },
    [updateTasksByIds]
  );

  const handleSetSelectionAnchor = useCallback((taskId: string | null) => {
    setSelectionAnchorId(taskId);
  }, []);

  const contextValue = useMemo<BatchTaskContextValue>(
    () => ({
      tasks,
      selectedTaskIds,
      selectionAnchorId,
      importFromCsv,
      addPromptRows,
      clearTasks,
      removeTasks,
      setSelection,
      addToSelection,
      removeFromSelection,
      setSelectionAnchor: handleSetSelectionAnchor,
      updateTasksByIds,
      setModelForTasks,
      setFormValuesForTasks,
      updateFormValuesForTasks,
      updateImagesForTasks,
    }),
    [
      tasks,
      selectedTaskIds,
      selectionAnchorId,
      importFromCsv,
      addPromptRows,
      clearTasks,
      removeTasks,
      setSelection,
      addToSelection,
      removeFromSelection,
      handleSetSelectionAnchor,
      updateTasksByIds,
      setModelForTasks,
      setFormValuesForTasks,
      updateFormValuesForTasks,
      updateImagesForTasks,
    ]
  );

  return <BatchTaskContext.Provider value={contextValue}>{children}</BatchTaskContext.Provider>;
};

export const useBatchTasks = (): BatchTaskContextValue => {
  const ctx = useContext(BatchTaskContext);
  if (!ctx) {
    throw new Error('useBatchTasks must be used within a BatchTaskProvider');
  }
  return ctx;
};
